/**
 * Pricing / GST display utilities.
 *
 * GST handling for Australian (and any single-rate) pricing:
 * - Database stores ex-GST (accounting standard; Xero/Stripe depend on it)
 * - UI displays GST-INCLUSIVE with a clear "inc GST" label (customer standard,
 *   and required for consumer pricing under Australian Consumer Law)
 *
 * This is the single shared "gross-up + format + label" helper the display
 * surfaces route through. The rate defaults to the active tenant's GST rate
 * (tenant.business.defaultTaxRate, via getTenantTaxRateSync) so a non-AU tenant
 * formats in their own rate; pass an explicit `rate` when you already have the
 * tenant in React context (getTenantTaxRate(tenant)).
 *
 * The currency symbol + number formatting route through the tenant's currency
 * (getCurrencySync) and locale (getLocaleSync) rather than a hardcoded '$'.
 *
 * 🔒 FINANCIAL VISIBILITY. Every formatter here returns `FINANCIAL_MASK`
 * ("••••") when the signed-in staff member isn't cleared to see money — see
 * src/utils/financialVisibility.ts for the policy and the customer-facing
 * carve-out. Masking lives HERE, in the shared formatters, so the ~50
 * surfaces that already route through them are covered without each one
 * having to remember the gate.
 *
 * ⚠️ That makes these DISPLAY-ONLY functions. Never use their output to build
 * a value that gets written to Firestore, an email, an SMS or a PDF — the
 * viewer's permission would be baked into stored data. Compute with the raw
 * numbers (`addGST`/`removeGST`/`calculateGST` are unmasked on purpose) and
 * format server-side for anything that leaves the browser.
 */

import { getTenantTaxRateSync, taxRateLabel } from '@/utils/taxRate';
import { getCurrencySync } from '@/utils/currency';
import { getLocaleSync } from '@/utils/locale';
import {
  FINANCIAL_MASK,
  shouldMaskFinancials,
} from '@/utils/financialVisibility';

/** Re-export so callers have one import for "GST (10%)"-style labels. */
export { taxRateLabel };
/** Re-export so a call site can render the same placeholder for a null amount. */
export { FINANCIAL_MASK };

/**
 * Digit options passed through to Intl.NumberFormat. Lets a call site keep a
 * whole-dollar stat tile (`{ maximumFractionDigits: 0 }`) or force cents on a
 * total, without hand-rolling `toLocaleString({ style: 'currency' })` again.
 */
export interface CurrencyDigits {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * Format a bare dollar amount in the tenant's currency + locale (no GST label).
 * Replaces the literal `$${n.toFixed(2)}` pattern so a non-AUD tenant formats
 * correctly.
 *
 * Returns `FINANCIAL_MASK` for staff without financial visibility.
 */
export function formatCurrency(amount: number, digits?: CurrencyDigits): string {
  if (shouldMaskFinancials()) return FINANCIAL_MASK;
  return formatCurrencyUnmasked(amount, digits);
}

/**
 * The raw formatter, with NO visibility check.
 *
 * Only for surfaces that have already established the viewer may see money —
 * today that's the `<Money>` component (which resolves the permission
 * reactively via context) and the customer-facing public pages. Everything
 * else wants `formatCurrency`.
 */
export function formatCurrencyUnmasked(
  amount: number,
  digits?: CurrencyDigits,
): string {
  const value = Number.isFinite(amount) ? amount : 0;
  try {
    return new Intl.NumberFormat(getLocaleSync(), {
      style: 'currency',
      currency: getCurrencySync(),
      ...digits,
    }).format(value);
  } catch {
    // Bad currency/locale code — fall back to a plain $ format rather than throw.
    return `$${value.toFixed(2)}`;
  }
}

/**
 * Convert ex-GST price to GST-inclusive.
 * @param exGST - Price excluding GST
 * @param rate - GST rate (decimal). Defaults to the active tenant rate.
 */
export function addGST(exGST: number, rate: number = getTenantTaxRateSync()): number {
  return Math.round(exGST * (1 + rate) * 100) / 100;
}

/**
 * Convert GST-inclusive price to ex-GST.
 * @param inclusive - Price including GST
 * @param rate - GST rate (decimal). Defaults to the active tenant rate.
 */
export function removeGST(inclusive: number, rate: number = getTenantTaxRateSync()): number {
  return Math.round((inclusive / (1 + rate)) * 100) / 100;
}

/**
 * Calculate GST amount from ex-GST price.
 * @param exGST - Price excluding GST
 * @param rate - GST rate (decimal). Defaults to the active tenant rate.
 */
export function calculateGST(exGST: number, rate: number = getTenantTaxRateSync()): number {
  return Math.round(exGST * rate * 100) / 100;
}

/**
 * Format an ex-GST price as a GST-INCLUSIVE, clearly-labelled string —
 * "$1,234.00 inc GST". The default for every customer-facing money render.
 * @param exGST - Price excluding GST
 * @param rate - GST rate (decimal). Defaults to the active tenant rate.
 */
export function formatIncGst(exGST: number, rate: number = getTenantTaxRateSync()): string {
  if (shouldMaskFinancials()) return FINANCIAL_MASK;
  return `${formatCurrencyUnmasked(addGST(exGST, rate))} inc GST`;
}

/**
 * Format an already-inc-GST amount with the "inc GST" label (no grossing-up).
 * Use when the value you hold is ALREADY GST-inclusive (e.g. deal.value,
 * extractedTotals.total, an invoice totalIncGst).
 */
export function formatAlreadyIncGst(incGst: number): string {
  if (shouldMaskFinancials()) return FINANCIAL_MASK;
  return `${formatCurrencyUnmasked(incGst)} inc GST`;
}

/**
 * Format an ex-GST price with an explicit "ex GST" label — "$1,234.00 ex GST".
 * Use on internal/line-item surfaces where the ex-GST figure is the right one
 * to show (e.g. a single line item that Xero will add GST to).
 */
export function formatExGst(exGST: number): string {
  if (shouldMaskFinancials()) return FINANCIAL_MASK;
  return `${formatCurrencyUnmasked(exGST)} ex GST`;
}

/**
 * Format price with GST label.
 * @param price - Price (ex-GST)
 * @param showInclusive - Whether to show inclusive GST (default true)
 * @param rate - GST rate (decimal). Defaults to the active tenant rate.
 * @returns Formatted price string
 */
export function formatPrice(
  price: number,
  showInclusive: boolean = true,
  rate: number = getTenantTaxRateSync(),
): string {
  return showInclusive ? formatIncGst(price, rate) : formatExGst(price);
}

/**
 * Format price range with GST.
 * @param minPrice - Minimum price (ex-GST)
 * @param maxPrice - Maximum price (ex-GST)
 * @param showInclusive - Whether to show inclusive GST (default true)
 * @param rate - GST rate (decimal). Defaults to the active tenant rate.
 * @returns Formatted price range string
 */
export function formatPriceRange(
  minPrice: number,
  maxPrice: number,
  showInclusive: boolean = true,
  rate: number = getTenantTaxRateSync(),
): string {
  if (shouldMaskFinancials()) return FINANCIAL_MASK;
  if (showInclusive) {
    return `${formatCurrencyUnmasked(addGST(minPrice, rate))} - ${formatCurrencyUnmasked(
      addGST(maxPrice, rate),
    )} inc GST`;
  }
  return `${formatCurrencyUnmasked(minPrice)} - ${formatCurrencyUnmasked(maxPrice)} ex GST`;
}

/**
 * Format price with both ex-GST and inclusive GST.
 * @param price - Price (ex-GST)
 * @param rate - GST rate (decimal). Defaults to the active tenant rate.
 * @returns Object with both formats
 */
export function formatPriceBoth(
  price: number,
  rate: number = getTenantTaxRateSync(),
): {
  exGST: string;
  incGST: string;
  gstAmount: string;
} {
  return {
    exGST: formatCurrency(price),
    incGST: formatCurrency(addGST(price, rate)),
    gstAmount: formatCurrency(calculateGST(price, rate)),
  };
}
